// Split out of src/components/Onboarding.jsx (docs/FILE_SPLIT_RUN1_SPEC.md, move-only).
// Onboarding.jsx re-exports everything it exported before, so existing imports keep working.

import { EditPencilIcon, Icon, ChildMark } from "../Common.jsx";
import { CARD_COLORS } from "../../lib/constants.js";
import { effectiveAge } from "../../lib/age.js";
import { cardGradientStyle, getKidTheme } from "../../lib/utils.js";
import { t as ct } from "../../lib/content.js";

const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

/* --------------------------------- root ---------------------------------- */

// ChildChipRow (the wizard's tappable child-chip row) was removed 8 Sep (LB-122): unreferenced
// since ChildContextBar replaced it on 30 Aug. History is in git. The slice boundary the
// harness uses to lift Onboarding out of this file (scripts/check-entry-blockers.mjs) is now
// ProfileSwitcherSheet below.

// Shared "pick a profile" sheet body — used by both switchers below. `entries` is already
// filtered/ordered by the caller (ParentProfileSwitcher passes every profile with hidden ones
// dimmed; KidProfileSwitcher passes only visibleProfiles, since a child should never even see
// a sibling's profile exists if a parent hid it on this device — the ORIGINAL ChildChipRow
// enforced that same rule via App.jsx's chipProfiles/visibleProfiles split, not inside the
// component itself, so this preserves it at the same layer rather than moving the rule).
// onAddChild (R-B addendum, Resolution 4): optional — when passed, a "+" row is appended
// after every profile entry that closes this sheet and hands control back to the caller to
// reveal the add-child form. Parent-mode only: ParentProfileSwitcher passes it,
// KidProfileSwitcher never does (a child adding a sibling isn't a thing), so this stays
// undefined and the row simply doesn't render there.
export function ProfileSwitcherSheet({ entries, activeChildId, onSelect, onClose, hiddenIds, showHiddenBadge, onAddChild }) {
  return (
    // R item follow-up (Alan, Aug 2026): was items-end (bottom-anchored) on mobile — got
    // obscured by the browser's OWN bottom toolbar (Safari's tab/nav bar) on a device that
    // hasn't installed the app as a PWA yet, which sits in exactly that screen region and
    // isn't something the app can draw over. Top-anchored instead — clear of any browser
    // chrome regardless of install state, top or bottom.
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start sm:items-center justify-center p-4" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 16px)" }} onClick={onClose}>
      {/* role="dialog" + aria-label, same pattern as every other modal in this codebase
          (the notification opt-in prompts, the undo-transaction confirm) — beyond the
          accessibility win, it also disambiguates this heading from the trigger pill's
          own "Switch profile" label, which stays in the DOM (just visually covered)
          while the sheet is open and would otherwise collide on a plain text locator. */}
      <div role="dialog" aria-label="Switch profile" className="bg-white rounded-2xl p-3 w-full max-w-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="text-xs font-medium text-slate-500 px-2 mb-2">{ct("profile_switcher.switch_profile", "Switch profile")}</div>
        <div className="flex flex-col gap-1.5">
          {entries.map((p) => {
            const isActive = p.id === activeChildId;
            const colorDef = CARD_COLORS[p.cardColor];
            const style = isActive
              ? { background: cardGradientStyle(p.cardColor, getKidTheme(effectiveAge(p))) }
              : colorDef ? { backgroundColor: `${colorDef.swatch}22` } : undefined;
            const hidden = showHiddenBadge && hiddenIds?.includes(p.id);
            return (
              <button
                key={p.id}
                onClick={() => { onSelect(p.id); onClose(); }}
                style={{ ...style, backgroundClip: "padding-box" }}
                className={`flex items-center gap-3 rounded-xl px-3 py-2.5 text-left ${isActive ? "text-white" : colorDef ? "" : "bg-slate-100"} ${hidden ? "opacity-50" : ""}`}
              >
                <ChildMark profile={p} size="sm" />
                <span className={`font-semibold text-sm flex-1 min-w-0 truncate ${isActive ? "text-white" : "text-slate-700"}`}>{p.name}</span>
                {hidden && <span className="text-xs flex-shrink-0" title="Hidden on this device"><Icon id="ic-lock" size={12} style={{ color: "currentColor" }} /></span>}
                {isActive && <Icon id="ic-check" size={14} className="flex-shrink-0" style={{ color: "currentColor" }} />}
              </button>
            );
          })}
          {onAddChild && (
            <button
              onClick={() => { onAddChild(); onClose(); }}
              className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-left bg-emerald-50 text-slate-900"
            >
              <span className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center font-bold text-emerald-700 flex-shrink-0">+</span>
              <span className="font-semibold text-sm flex-1 min-w-0 truncate">{ct("profile_switcher.add_child", "Add child profile")}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// R item (Alan, Aug 2026): replaces ChildChipRow in the App shell's main sticky top bar for
// Parent Mode — the always-expanded row of chips (up to ~76px) collapses to one ~52px pill
// showing just the active child, tapped open into the sheet above for anyone else. The WHOLE
// pill is the tap target (not just the "Switch profile" label) per Alan's explicit ask for a
// large click zone — there's no small chevron or icon anyone has to aim for.
//
// `profiles` here is every profile in the household (unfiltered) — a parent needs to be able
// to select ANY child regardless of hidden-on-this-device status to manage them, exactly as
// the original ChildChipRow's Parent Mode behaviour worked. Hidden ones are dimmed with the
// same 🙈 badge the old chips used, now inside the sheet rather than inline.
//
// NOTE: still used only in App.jsx's persistent top bar — Wizard.jsx's own two ChildChipRow
// placements (steps 4 and 5, picking which child to configure while setting up the household)
// are deliberately UNCHANGED. That's a real scope question flagged rather than decided
// silently: an always-visible row arguably still suits actively comparing/switching between
// several kids mid-setup better than a collapsed switcher does — worth a separate call.
// R item (Alan, Aug 2026): replaces ChildChipRow in the App shell's main sticky top bar for
// Parent Mode — the always-expanded row of chips (up to ~76px) collapses to one ~52px pill
// showing just the active child, tapped open into the sheet above for anyone else. The WHOLE
// pill is the tap target (not just the "Switch profile" label) per Alan's explicit ask for a
// large click zone — there's no small chevron or icon anyone has to aim for.
//
// `profiles` here is every profile in the household (unfiltered) — a parent needs to be able
// to select ANY child regardless of hidden-on-this-device status to manage them, exactly as
// the original ChildChipRow's Parent Mode behaviour worked. Hidden ones are dimmed with the
// same 🙈 badge the old chips used, now inside the sheet rather than inline.
//
// NOTE: still used only in App.jsx's persistent top bar — Wizard.jsx's own two ChildChipRow
// placements (steps 4 and 5, picking which child to configure while setting up the household)
// are deliberately UNCHANGED. That's a real scope question flagged rather than decided
// silently: an always-visible row arguably still suits actively comparing/switching between
// several kids mid-setup better than a collapsed switcher does — worth a separate call.
// onAddChild (R-B addendum, Resolution 4): optional — passed through to the switcher sheet's
// "+" row. The Kids tab mounts this with onAddChild wired to reveal ProfilesTab's own
// (unconditionally rendered) "+ Add child profile" form, so no new add-child plumbing exists
// here beyond that reveal signal.
export function ParentProfileSwitcher({ profiles, activeChildId, onSelect, hiddenIds, onAddChild }) {
  const [open, setOpen] = useState(false);
  const active = profiles.find((p) => p.id === activeChildId) || profiles[0];
  if (!active) return null;
  const style = { background: cardGradientStyle(active.cardColor, getKidTheme(effectiveAge(active))) };
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        style={style}
        className="w-full flex items-center gap-3 rounded-2xl px-4 py-3 text-white shadow-sm"
      >
        <ChildMark profile={active} size="sm" />
        <span className="font-semibold text-base flex-1 min-w-0 truncate text-left">{active.name}</span>
        <span className="font-semibold text-xs bg-white/20 rounded-full px-3 py-1.5 flex-shrink-0">{ct("profile_switcher.switch_profile", "Switch profile")}</span>
      </button>
      {open && (
        <ProfileSwitcherSheet
          entries={profiles}
          activeChildId={activeChildId}
          onSelect={onSelect}
          onClose={() => setOpen(false)}
          hiddenIds={hiddenIds}
          showHiddenBadge
          onAddChild={onAddChild}
        />
      )}
    </>
  );
}

// R item (Alan, Aug 2026), Kid Mode's compact switcher — a slim trigger sitting in the body
// between the header and the hero card, NOT the header itself (already full: logo + Help/
// Parent Mode).
//
// R item follow-up (Alan, Aug 2026): now ALWAYS renders the active child's avatar + name —
// previously this returned null outright for a solo visible child, on the logic that
// HeroCard's own name-under-avatar label covered that case. HeroCard's label is gone now
// (the child's name needed exactly one home, not two), so this row is it: the single place
// that answers "who am I" for every device, solo child or not. Only the "Switch kid ›" pill
// — and the ability to open the picker sheet at all — is conditional on there actually being
// someone else to switch to; with one visible child the row is present but inert, which
// keeps the layout stable if a second child is later made visible on this device (no jump
// from "no row" to "row appears").
//
// Follow-up (Alan): the original version showed an anonymous overlapping-circles STACK of
// every visible child with a plain "Switch kid" text label — didn't say who's active, and
// didn't read as tappable. Now shows the ACTIVE child's own avatar + name and "Switch kid ›"
// as a bordered, tinted pill with a trailing chevron — same "this is a clickable link" visual
// language the app already uses elsewhere (e.g. PigCard's "Oink Shop ›"), not plain grey
// label text.
//
// `profiles` MUST be pre-filtered to visibleProfiles by the caller, same as the original
// ChildChipRow's Kid Mode behaviour (App.jsx's chipProfiles ternary) — a child must never be
// able to see or switch to a sibling's profile a parent hid on this device, so there's no
// hiddenIds prop here at all (unlike the parent switcher above): this component has no way
// to even learn a hidden sibling exists, which is the point.
// `onEditAvatar` (pass N, Alan): the pencil that edits the avatar and card colour lives here,
// next to the avatar it edits — not on the hero, which shows the equipped character.
// `trailing` (8 Sep): a slot at the right end of the row — Kid Mode parks the phone-width
// Install icon here so the header row keeps the head and wordmark at 390px.
export function KidProfileSwitcher({ profiles, activeChildId, onSelect, onEditAvatar, trailing = null }) {
  const [open, setOpen] = useState(false);
  if (profiles.length === 0) return null;
  const active = profiles.find((p) => p.id === activeChildId) || profiles[0];
  const canSwitch = profiles.length > 1;
  return (
    <div className="mb-3 flex items-center gap-2.5" data-testid="kid-name-row">
      {/* 8 Sep (Alan): name on one line, "Switch kid" its own line beneath. Three small tap
          targets rather than one (character, name and caption all open the picker; nested
          buttons aren't allowed), and the character button is labelled with the child's name
          only so the caption stays the one button named "Switch kid" — several specs and the
          kid-sheet harness match it by that exact name.

          18 Sep (Alan): the pencil moved OFF the name and up against the CHARACTER, which is
          what it edits. Beside the name it read as "rename me". It keeps a 4px gap and no
          horizontal padding, so there is a dead strip between the two: tapping the character
          opens the picker, and that strip is what stops a thumb aimed at the pencil switching
          kid instead. No negative margins, no pseudo-element hit areas.

          A longer pass this morning made the name much larger and bottom-aligned the row to
          the character. It was reverted: the character's sprite reserves about 18% of its box
          as headroom for hats, so a bare character always reads smaller than its box and no
          amount of type sizing against that box balances. See ChildMark if that comes up
          again. */}
      {canSwitch ? (
        <>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button onClick={() => setOpen(true)} aria-label={active.name} className="flex-shrink-0"><ChildMark profile={active} size="md" /></button>
            {onEditAvatar && (
              <button onClick={onEditAvatar} data-testid="edit-character" aria-label="Edit avatar and card colour" title="Edit avatar and card colour" className="w-7 h-7 rounded-full bg-white border border-slate-200 flex items-center justify-center flex-shrink-0 text-slate-500">
                <EditPencilIcon width={12} height={12} />
              </button>
            )}
          </div>
          <div className="min-w-0 leading-tight">
            <button onClick={() => setOpen(true)} className="block font-extrabold text-base text-slate-900 truncate text-left max-w-full">{active.name}</button>
            <button onClick={() => setOpen(true)} className="block text-xs font-bold" style={{ color: "var(--kid-pop, #1B2A4A)" }}>{ct("profile_switcher.switch_kid", "Switch kid")}</button>
          </div>
        </>
      ) : (
        <>
          {/* Nothing to switch to: no button semantics on the character or the name. The
              pencil still pairs with the character, same as the switchable branch. */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <ChildMark profile={active} size="md" />
            {onEditAvatar && (
              <button onClick={onEditAvatar} data-testid="edit-character" aria-label="Edit avatar and card colour" title="Edit avatar and card colour" className="w-7 h-7 rounded-full bg-white border border-slate-200 flex items-center justify-center flex-shrink-0 text-slate-500">
                <EditPencilIcon width={12} height={12} />
              </button>
            )}
          </div>
          <span className="font-extrabold text-base text-slate-900 truncate min-w-0">{active.name}</span>
        </>
      )}
      {trailing && <div className="ml-auto flex-shrink-0 self-center">{trailing}</div>}
      {open && (
        <ProfileSwitcherSheet
          entries={profiles}
          activeChildId={activeChildId}
          onSelect={onSelect}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}
